import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { BookMarked, BookOpen, Users, Video, GraduationCap, TrendingUp } from 'lucide-react';

export default function ProgrammeManagerDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/academic/programmes').catch(() => ({ data: [] })),
      api.get('/academic/courses').catch(() => ({ data: [] })),
    ]).then(([progRes, courseRes]) => {
      const programmes = Array.isArray(progRes.data) ? progRes.data : (progRes.data?.programmes || []);
      const courses = Array.isArray(courseRes.data) ? courseRes.data : (courseRes.data?.courses || []);
      setStats({ programmes: programmes.length, courses: courses.length });
    }).finally(() => setLoading(false));
  }, []);

  const cards = [
    { label: 'Programmes', value: stats?.programmes ?? '—', icon: BookMarked, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Courses', value: stats?.courses ?? '—', icon: BookOpen, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Active Students', value: '—', icon: Users, color: 'text-violet-600', bg: 'bg-violet-50' },
    { label: 'Live Classes', value: '—', icon: Video, color: 'text-rose-600', bg: 'bg-rose-50' },
  ];

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-xl font-semibold text-slate-800">Programme Manager</h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage your programmes, courses, and student progress</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map(({ label, value, icon: Icon, color, bg }) => (
          <Card key={label} className="border-slate-100">
            <CardContent className="pt-5">
              <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3`}>
                <Icon className={`w-4 h-4 ${color}`} />
              </div>
              <p className="text-2xl font-bold text-slate-800">{loading ? '…' : value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-slate-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-[#1976D2]" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {[
                { label: 'View Programmes', href: '/programme-manager/programmes' },
                { label: 'Manage Courses', href: '/programme-manager/courses' },
                { label: 'Course Lessons', href: '/programme-manager/lessons' },
                { label: 'Attendance Records', href: '/programme-manager/attendance' },
                { label: 'Live Classes', href: '/live' },
              ].map(({ label, href }) => (
                <a
                  key={label}
                  href={href}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-50 text-sm text-slate-700 transition-colors"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1976D2]" />
                  {label}
                </a>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#1976D2]" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-slate-400 text-center py-8">
              Activity feed coming soon
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
